import React, { useState, useId, useRef } from 'react';
import { useMergeRefs } from '@floating-ui/react';
import classNames from 'classnames';
import IconButton from '../../../icon-button';
import { useTreeSelectContext } from '../../context/tree-select-context';
import keyNames from '../../../../constants/key-names';

const TreeSelectTrigger = () => {
  const {
    listboxId,
    inputValue,
    selectionCount,
    searchPlaceholder,
    isOpen,
    disabled,
    clearable,
    inputRef,
    listRef,
    inputId,
    placeholder,
    activeId,
    isFirstRow,
    onBlur,
    onFocus,
    onKeyDown,
    getSelectionLabel,
    setIsOpen,
    setInputValue,
    navigateToFirst,
    onChange,
    setActiveId,
    navigateToNext,
    navigateToPrev,
  } = useTreeSelectContext();
  const ref = useRef(null);
  const mergedRef = useMergeRefs([inputRef, ref]);

  const [isFocused, setIsFocused] = useState(false);

  const elementId = useId();
  const id = inputId || elementId;

  const hasClear = clearable && selectionCount > 0 && !disabled;
  const hasSelection = selectionCount > 0;
  const selectionLabel = getSelectionLabel(selectionCount);
  const displayValue = isFocused ? inputValue : '';
  let placeholderValue = placeholder;

  if (isFocused) {
    placeholderValue = searchPlaceholder;
  } else if (hasSelection) {
    placeholderValue = selectionLabel;
  }

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputFocus = (e) => {
    onFocus?.(e);
    setIsFocused(true);
    setActiveId(null);
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const handleInputBlur = (e) => {
    onBlur?.(e);
    setIsFocused(false);
  };

  const handleInputKeyDown = (e) => {
    onKeyDown?.(e);
    if (!isOpen) return;

    if (e.key === keyNames.UP) {
      e.preventDefault();
      if (isFirstRow) {
        setActiveId(null);
        return;
      }

      if (activeId) {
        navigateToPrev();
        listRef.current?.focus();
      }
      return;
    }

    if (e.key === keyNames.DOWN) {
      e.preventDefault();
      if (activeId) {
        navigateToNext();
      } else {
        navigateToFirst();
      }
      listRef.current?.focus();
    }
  };

  const handleClearClick = (e) => {
    e.preventDefault();
    onChange([]);
    setInputValue('');
  };

  const handleArrowClick = (e) => {
    e.preventDefault();
    if (isOpen) {
      setIsOpen(false);
      ref.current?.blur();
    } else {
      ref.current?.focus();
    }
  };

  return (
    <label htmlFor={id} className="ws-ui-tree-select__trigger">
      <input
        id={id}
        ref={mergedRef}
        className={classNames('ws-ui-tree-select__trigger-input', {
          'ws-ui-tree-select__trigger-input--has-selection': hasSelection,
        })}
        type="text"
        value={displayValue}
        placeholder={placeholderValue}
        disabled={disabled}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        role="combobox"
        aria-autocomplete="list"
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        onFocus={handleInputFocus}
        onKeyDown={handleInputKeyDown}
        autoComplete="off"
      />
      <div className="ws-ui-tree-select__trigger-indicators">
        {hasClear && (
          <IconButton
            transparent
            glyph="clear-search"
            size="medium"
            tabIndex={-1}
            onClick={handleClearClick}
          />
        )}
        <IconButton
          disabled={disabled}
          transparent
          glyph={isOpen ? 'up-small' : 'down-small'}
          size="medium"
          tabIndex={-1}
          onMouseDown={handleArrowClick}
        />
      </div>
    </label>
  );
};

export default TreeSelectTrigger;
